import type { BlockFeature, DrilledHole, DrillHole } from '~api/services';
import { getHardnessColour, HARDNESS_COLOURS } from '~config';
import type { BaseColour } from '~types';
import {
  type ColourScale,
  type ColourValue,
  createContinuousScale,
  createDiscreteScale,
  createHeatmapColourValue,
  createHeatmapScale,
  DEFAULT_COLOUR,
} from '~utils';

/** Properties that use discrete (categorical) colour scales */
const BLOCK_DISCRETE_PROPERTIES = ['strand', 'material', 'geology', 'hardness'] as const;

/** Properties for drilled holes that use discrete (categorical) colour scales */
const HOLE_DISCRETE_PROPERTIES = [
  'cavity',
  'destination',
  'hazardous',
  'holetype',
  'strandtype',
  'source_name',
] as const;

/** Hardness levels in display order (using actual data values) */
const HARDNESS_LEVELS = Object.keys(HARDNESS_COLOURS) as (keyof typeof HARDNESS_COLOURS)[];

const FULL_CIRCLE_DEGREES = 360;

/**
 * Wraps any bearing value into [0, 360) using the double-modulo idiom to
 * correctly handle negative inputs (e.g. -10° → 350°).
 */
const wrapBearing = (value: number): number =>
  ((value % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;

/**
 * Returns a normalise function that remaps bearings to a contiguous linear range
 * so they can be passed to a standard min/max colour scale without the 0°/360°
 * boundary causing discontinuities.
 *
 * The algorithm:
 * 1. Wrap all bearings to [0, 360) and sort them.
 * 2. Find the largest empty arc between consecutive bearings (including the
 *    wrap-around gap from the last bearing back to the first).
 * 3. "Cut" the circle at that gap: bearings before the cut point are shifted
 *    up by 360° so the entire dataset falls in one contiguous interval.
 *
 * Example: bearings [1°, 359°] have a large gap from 1° to 359°. The cut
 * point is 1°, so 359° stays at 359° and 1° shifts to 361°, giving a span of
 * only 2° instead of the misleading 358° that a raw min/max would produce.
 */
const createBearingNormaliser = (bearings: number[]): ((value: number) => number) => {
  if (!bearings.length) return wrapBearing;

  const sorted = bearings.map(wrapBearing).sort((a, b) => a - b);

  let largestGap = -1;
  let cutPoint = sorted[0];

  for (let index = 0; index < sorted.length; index += 1) {
    const next = sorted[(index + 1) % sorted.length];
    // Circular gap formula works for both adjacent and wrap-around cases
    const gap = (next - sorted[index] + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;

    if (gap > largestGap) {
      largestGap = gap;
      cutPoint = next;
    }
  }

  return (value: number) => {
    const wrapped = wrapBearing(value);
    return wrapped < cutPoint ? wrapped + FULL_CIRCLE_DEGREES : wrapped;
  };
};

/**
 * Creates a colour scale for block features based on a property.
 * Automatically selects discrete or heatmap scale based on property type.
 * For 'hardness', uses predefined colours from config. For other discrete properties, generates categories from data.
 *
 * @param {BlockFeature[]} blocks - The block features to create the scale for
 * @param {string} property - The property name to base the colour scale on
 * @return A ColourScale object for the specified property, or undefined if blocks are empty
 */
function createBlockColourScale(blocks: BlockFeature[], property: string): ColourScale | undefined {
  if (!blocks.length) return undefined;

  if (property === 'hardness') {
    const customMapping: Record<string, ColourValue> = {};
    for (const level of HARDNESS_LEVELS) {
      const config = getHardnessColour(level);
      customMapping[level] = { fill: config.fill, stroke: config.stroke, opacity: config.opacity };
    }

    const data = HARDNESS_LEVELS.map((h) => ({ hardness: h }));
    const scale = createDiscreteScale(data, 'hardness', {
      customMapping,
      sortValues: (a, b) =>
        HARDNESS_LEVELS.indexOf(a as keyof typeof HARDNESS_COLOURS) -
        HARDNESS_LEVELS.indexOf(b as keyof typeof HARDNESS_COLOURS),
    });

    // Use labels from config
    return {
      ...scale,
      legend: scale.legend.map((item) => ({
        ...item,
        label: HARDNESS_COLOURS[item.value as keyof typeof HARDNESS_COLOURS]?.label || item.value,
      })),
    };
  }

  if (BLOCK_DISCRETE_PROPERTIES.includes(property as (typeof BLOCK_DISCRETE_PROPERTIES)[number])) {
    // Other discrete properties
    const data = blocks
      .filter((b) => b.properties[property as keyof typeof b.properties] != null)
      .map((b) => ({ [property]: String(b.properties[property as keyof typeof b.properties]) }));
    return createDiscreteScale(data, property);
  }

  // Numeric properties - heatmap
  const data = blocks
    .map((b) => ({ [property]: Number(b.properties[property as keyof typeof b.properties]) }))
    .filter((d) => !Number.isNaN(d[property]));
  if (!data.length) return undefined;
  return createHeatmapScale(data, property, { steps: 5, formatLabel: (v) => v.toFixed(3) });
}

/**
 * Gets the colour for a block based on a colour scale.
 * If the colour scale is discrete, looks up the block's property value.
 * If it's continuous, gets the numeric value and maps it to a colour.
 *
 * @param {BlockFeature} block - The block feature to get the colour for
 * @param {ColourScale | undefined} colourScale - The colour scale to use for determining the block's colour
 * @return The ColourValue for the block based on the colour scale, or a default colour if no scale is provided
 */
function getBlockColour(block: BlockFeature, colourScale: ColourScale | undefined): ColourValue {
  if (!colourScale) return DEFAULT_COLOUR;

  if (colourScale.type === 'discrete') {
    const value = String(block.properties[colourScale.property as keyof typeof block.properties]);
    return colourScale.getColour(value);
  }

  // Continuous scale - convert string colour to ColourValue
  const value = Number(block.properties[colourScale.property as keyof typeof block.properties]);
  const colour = colourScale.getColour(value);
  return createHeatmapColourValue(colour);
}

/**
 * Creates a colour scale for drilled holes based on a property.
 * Automatically selects discrete or heatmap scale based on property type.
 * For 'hardness', uses predefined colours from config. For other discrete properties, generates categories from data.
 *
 * @param {DrilledHole[]} holes - The drilled holes to create the scale for
 * @param {string} property - The property name to base the colour scale on
 * @return A ColourScale object for the specified property, or undefined if holes are empty
 */
function createDrilledHoleColourScale(holes: DrilledHole[], property: string): ColourScale | undefined {
  if (!holes.length) return undefined;

  if (property === 'hardness' || property === 'hardness_planned') {
    const customMapping: Record<string, ColourValue> = {};
    for (const level of HARDNESS_LEVELS) {
      const config = getHardnessColour(level);
      customMapping[level] = { fill: config.fill, stroke: config.stroke, opacity: config.opacity };
    }

    const data = HARDNESS_LEVELS.map((h) => ({ hardness: h, hardness_planned: h }));
    const scale = createDiscreteScale(data, property, {
      customMapping,
      sortValues: (a, b) =>
        HARDNESS_LEVELS.indexOf(a as keyof typeof HARDNESS_COLOURS) -
        HARDNESS_LEVELS.indexOf(b as keyof typeof HARDNESS_COLOURS),
    });

    // Use labels from config
    return {
      ...scale,
      legend: scale.legend.map((item) => ({
        ...item,
        label: HARDNESS_COLOURS[item.value as keyof typeof HARDNESS_COLOURS]?.label || item.value,
      })),
    };
  }

  if (HOLE_DISCRETE_PROPERTIES.includes(property as (typeof HOLE_DISCRETE_PROPERTIES)[number])) {
    // Other discrete properties
    const data = holes
      .filter((h) => h[property as keyof typeof h] != null)
      .map((h) => ({ [property]: String(h[property as keyof typeof h]) }));
    return createDiscreteScale(data, property);
  }

  // Numeric properties - heatmap
  const data = holes
    .map((h) => ({ [property]: Number(h[property as keyof typeof h]) }))
    .filter((d) => !Number.isNaN(d[property]));

  if (!data.length) return undefined;

  return createHeatmapScale(data, property, { steps: 5, formatLabel: (v) => v.toFixed(3) });
}

/**
 * Creates a default colour scale for neighbouring drill holes.
 * Uses a discrete scale keyed by the selected categorical property.
 */
function createNeighbouringHoleColourScale(
  holes: DrillHole[],
  property: 'pattern_name' | 'drill_model' = 'pattern_name',
): ColourScale | undefined {
  if (!holes.length) return undefined;

  const holeData = holes
    .filter((hole) => hole[property] != null)
    .map((hole) => ({
      [property]: property === 'drill_model' ? String(hole[property]).toUpperCase() : String(hole[property]),
    }));

  if (!holeData.length) return undefined;

  return createDiscreteScale(holeData, property);
}

/**
 * Creates a continuous colour scale for neighbouring drill holes.
 * Values are read directly from DrillHole numeric properties.
 */
function createNeighbouringHoleContinuousColourScale(
  holes: DrillHole[],
  property: keyof DrillHole,
  formatLabel: (value: number) => string,
  baseColour?: BaseColour,
): ColourScale | undefined {
  if (!holes.length) return undefined;

  if (property === 'bearing') {
    const normalise = createBearingNormaliser(holes.map((hole) => hole.bearing));
    const scale = createContinuousScale(
      // Attach normalised bearing as a new field so createContinuousScale can use its standard min/max logic
      holes.map((hole) => ({ ...hole, bearing_normalised: normalise(hole.bearing) })),
      'bearing_normalised',
      { baseColour },
    );

    // Rebuild property name and labels using original (wrapped) bearing values
    return {
      ...scale,
      property: 'bearing',
      legend: scale.legend.map((entry) => ({ ...entry, label: formatLabel(wrapBearing(entry.value)) })),
      getColour: (value: number) => scale.getColour(normalise(value)),
    };
  }

  return createContinuousScale(holes, property, { formatLabel, baseColour });
}

/**
 * Gets the colour for a drilled hole based on a colour scale.
 * If the colour scale is discrete, looks up the hole's property value.
 * If it's continuous, gets the numeric value and maps it to a colour.
 *
 * @param {DrilledHole} hole - The drilled hole to get the colour for
 * @param {ColourScale | undefined} colourScale - The colour scale to use for determining the hole's colour
 * @return The ColourValue for the hole based on the colour scale, or a default colour if no scale is provided
 */
function getDrilledHoleColour(hole: DrilledHole, colourScale: ColourScale | undefined): ColourValue {
  if (!colourScale) return DEFAULT_COLOUR;

  if (colourScale.type === 'discrete') {
    const value = String(hole[colourScale.property as keyof typeof hole]);
    return colourScale.getColour(value);
  }

  // Continuous scale - convert string colour to ColourValue
  const value = Number(hole[colourScale.property as keyof typeof hole]);
  const colour = colourScale.getColour(value);
  return createHeatmapColourValue(colour);
}

export {
  createBlockColourScale,
  createDrilledHoleColourScale,
  createNeighbouringHoleColourScale,
  createNeighbouringHoleContinuousColourScale,
  getBlockColour,
  getDrilledHoleColour,
};
